import React, { useState } from 'react';
import {
  Box,
  Text,
  Input,
  Flex,
  Button,
  IconButton,
  Textarea,
  Select,
  RadioGroup,
  Menu,
  Tabs,
  Highlight,
} from '@chakra-ui/react';
import { CustomButton } from './UI/CustomButton';
import { MdDelete } from 'react-icons/md';
import { MdFileUpload, MdCancel, MdAttachFile, MdExpandMore } from 'react-icons/md';
import { useConstantsQuery } from '../query/useConstantsQuery';
import { formatConstraintValue, constraintTypeToFieldMap, fieldToDisplayNameMap } from '../utils/constraintFormat';

interface ValueChangeDetails<T = string> {
  value: T;
}

const languages = ['fr', 'en', 'it', 'de', 'nl', 'es'];
const genderOptions = ['Male', 'Female'];
const pathologyOptions = ['Osteoarthritis', 'Arthritis', 'Fracture'];
// Dynamic constraint options will be built from constants query

interface ProgramFormPanelProps {
  selectedNode: { id: string; label: string; children?: any[] } | null;
  formData: any;
  onChange: (data: any) => void;
  onBlur?: (data: any) => void;
  isPublished?: boolean;
  onAddExercise?: () => void;
  isEditing?: boolean;
}

function isStepOption(node: { id: string; label: string; children?: any[] } | null): boolean {
  if (!node) return false;
  return !node.children;
}

const ProgramFormPanel: React.FC<ProgramFormPanelProps> = ({ selectedNode, formData, onChange, onBlur, isPublished, onAddExercise, isEditing }) => {
  // Debug logging to see what data is passed
  console.log('ProgramFormPanel - selectedNode:', selectedNode);
  console.log('ProgramFormPanel - formData:', formData);
  console.log('ProgramFormPanel - formData.exercises:', formData?.exercises);
  console.log('ProgramFormPanel - exercises length:', formData?.exercises?.length);
  
  // Helper function to render status with conditional styling
  const renderStatus = (status: string) => {
    switch (status) {
      case 'published':
        return <Highlight styles={{ color: '#66D079', bgColor: '#66D07940', p: '8px', borderRadius: '10px', textTransform: 'capitalize', height: "18px", lineHeight: "18px", padding: '0 10px' }} query={status}>{status}</Highlight>;
      case 'draft':
        return <Highlight styles={{ color: '#51CBFD', bgColor: '#51CBFD40', p: '8px', borderRadius: '10px', textTransform: 'capitalize', height: "18px", lineHeight: "18px",  padding: '0 10px'  }} query={status}>{status}</Highlight>;
      default:
        return <Highlight query={status}>{status}</Highlight>;
    }
  };

  // State for language tab
  // State for language tab
  const [langTab, setLangTab] = useState('en');
  // State for poster type and file
  const [posterType, setPosterType] = useState<'upload' | 'url'>('url');
  const [posterFile, setPosterFile] = useState<File | null>(null);

  // State for constraints
  const [selectedConstraints, setSelectedConstraints] = useState<string[]>([]);
  const [selectedGender, setSelectedGender] = useState<string[]>([]);
  const [selectedPathology, setSelectedPathology] = useState<string[]>([]);

  // Fetch constants from database
  const { data: constantsData, isLoading: isLoadingConstants } = useConstantsQuery();

  // Build dynamic constraint options from constants data
  const allConstraintOptions = constantsData ? [
    { type: 'Gender', options: constantsData.genders?.map(formatConstraintValue) || [] },
    { type: 'Pathology', options: constantsData.pathologies?.map(formatConstraintValue) || [] },
    { type: 'Sport', options: constantsData.programSports?.map(formatConstraintValue) || [] },
    { type: 'Special program', options: constantsData.specialPrograms?.map(formatConstraintValue) || [] },
    { type: 'Week focus', options: constantsData.weekFocus?.map(formatConstraintValue) || [] },
    { type: 'User level', options: constantsData.userLevels?.map(formatConstraintValue) || [] },
    { type: 'Category', options: constantsData.categories?.map(formatConstraintValue) || [] },
    { type: 'Material', options: constantsData.materials?.map(formatConstraintValue) || [] },
  ] : [];

  // Get constraints from formData for step options, or use local state for program
  const activeConstraints = selectedNode && !selectedNode.children 
    ? (Array.isArray(formData?.constraints) ? formData.constraints : [])
    : ([] as { type: string; values: string[] }[]);
    
  const availableConstraintTypes = allConstraintOptions
    .map((opt) => opt.type)
    .filter((type: string) => !activeConstraints.some((c: any) => c.type === type));

  // Handler to add a new constraint type
  const handleAddConstraint = (type: string) => {
    if (selectedNode && !selectedNode.children) {
      // For step options, save to formData
      const newConstraints = [...activeConstraints, { type, values: [] }];
      onChange({ ...formData, constraints: newConstraints });
    }
  };

  // Handler to remove a constraint
  const handleRemoveConstraint = (type: string) => {
    if (selectedNode && !selectedNode.children) {
      // For step options, save to formData
      const newConstraints = activeConstraints.filter((c: any) => c.type !== type);
      onChange({ ...formData, constraints: newConstraints });
    }
  };

  // Helper function to convert formatted value back to original format
  const convertFormattedValueToOriginal = (formattedValue: string, type: string): string => {
    const fieldName = constraintTypeToFieldMap[type];
    if (!fieldName || !constantsData) return formattedValue;
    
    const originalValues = constantsData[fieldName as keyof typeof constantsData] as string[];
    if (!originalValues) return formattedValue;
    
    // Find the original value that matches the formatted value
    const originalValue = originalValues.find(val => formatConstraintValue(val) === formattedValue);
    return originalValue || formattedValue;
  };

  // Helper function to convert original value to formatted value
  const convertOriginalValueToFormatted = (originalValue: string): string => {
    return formatConstraintValue(originalValue);
  };

  // Handler to add a value to a constraint
  const handleAddConstraintValue = (type: string, formattedValue: string) => {
    if (selectedNode && !selectedNode.children) {
      // Convert formatted value back to original format for storage
      const originalValue = convertFormattedValueToOriginal(formattedValue, type);
      
      // For step options, save to formData
      const newConstraints = activeConstraints.map((c: any) => 
        c.type === type 
          ? { ...c, values: [...c.values, originalValue] }
          : c
      );
      onChange({ ...formData, constraints: newConstraints });
    }
  };

  // Handler to remove a value from a constraint
  const handleRemoveConstraintValue = (type: string, formattedValue: string) => {
    if (selectedNode && !selectedNode.children) {
      // Convert formatted value back to original format for removal
      const originalValue = convertFormattedValueToOriginal(formattedValue, type);
      
      // For step options, save to formData
      const newConstraints = activeConstraints.map((c: any) => 
        c.type === type 
          ? { ...c, values: c.values.filter((v: any) => v !== originalValue) }
          : c
      );
      onChange({ ...formData, constraints: newConstraints });
    }
  };

  // Handler for language tab change
  function handleLangTabChange(details: ValueChangeDetails<string>) {
    setLangTab(details.value);
  }

  // Handler for poster type radio switch (match AddExerciseDialog)
  function handlePosterTypeChange(details: ValueChangeDetails<'upload' | 'url'>) {
    setPosterType(details.value);
    // Also pass the upload type to parent for upload handling
    onChange({ 
      ...formData, 
      posterType: details.value 
    });
  }

  // Helper to find text data by language for multilingual fields
  function findTextDataByLanguage(section: 'description' | 'displayName', language: string) {
    return formData[section]?.find((item: { language: string }) => item.language === language);
  }

  // Helper to update text data by language for multilingual fields
  function updateTextDataByLanguage(section: 'description' | 'displayName', language: string, value: string) {
    // Ensure the section exists and is an array
    const currentSection = formData[section] || [];
    
    // Find if the language already exists
    const existingIndex = currentSection.findIndex((item: { language: string }) => item.language === language);
    
    let updatedSection;
    if (existingIndex !== -1) {
      // Update existing language entry
      updatedSection = currentSection.map((item: { language: string, text: string }, index: number) =>
        index === existingIndex ? { ...item, text: value } : item
      );
    } else {
      // Add new language entry
      updatedSection = [...currentSection, { language, text: value }];
    }
    
    onChange({ ...formData, [section]: updatedSection });
  }

  // Helper to update a nested field in formData
  function updateNestedField(section: string, field: string, value: any) {
    onChange({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value,
      },
    });
  }

  if (!selectedNode) {
    return (
      <Box p='12px 16px 0px 52px' flex={1} overflowY='auto'>
        <Flex align="center" justify="space-between" mb={6}>
          <Flex align="center" gap={3}>
            {renderStatus(isPublished ? 'published' : 'draft')}
            <Text fontSize="2xl" fontWeight="bold" ml='16px'>
              {isEditing ? `Edit ${formData?.name || 'Program'}` : 'Add New Program'}
            </Text>
          </Flex>
        </Flex>

        <Text fontWeight="medium" mb={2}>Generic name</Text>
        <Input
          placeholder="Generic name"
          value={formData?.name || ''}
          onChange={e => onChange({ ...formData, name: e.target.value })}
          onBlur={() => onBlur && onBlur(formData)}
          mb={6}
        />

        <Flex mb='24px'>
          <Text fontWeight="medium" mb={1} mr='32px'>Poster:</Text>
          <Flex flexDirection="column" flex={1}>
            <RadioGroup.Root
              colorPalette="pink"
              variant="outline"
              value={posterType}
              onValueChange={handlePosterTypeChange}
              mb='26px'
              display="flex"
              gap='48px'
            >
              <RadioGroup.Item value="upload">
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemIndicator />
                <RadioGroup.ItemText ml='8px'>Upload file</RadioGroup.ItemText>
              </RadioGroup.Item>
              <RadioGroup.Item value="url">
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemIndicator />
                <RadioGroup.ItemText ml='8px'>Image URL</RadioGroup.ItemText>
              </RadioGroup.Item>
            </RadioGroup.Root>
            {posterType === 'upload' ? (
              <Flex align="center" gap={3} mb={2}>
                {posterFile ? (
                  <CustomButton visual="ghost" border="1px solid #D0CFCF" borderRadius="6px" h='48px' onClick={() => {
                    setPosterFile(null);
                    updateNestedField('poster', 'source', '');
                    // Also clear the file from parent component
                    onChange({ 
                      ...formData, 
                      poster: { 
                        ...formData.poster, 
                        source: '' 
                      },
                      posterFile: null,
                      posterType: posterType
                    });
                  }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <MdCancel />
                    </Box>
                  </CustomButton>
                ) : (
                  <CustomButton as="label" visual="ghost" border="1px solid #D0CFCF" borderRadius="6px" h='48px'>
                    <Box display="flex" flexDirection="column" alignItems="center" fontWeight="medium" color="#8B8B8B" lineHeight="14px">
                      <MdFileUpload />
                      Upload
                    </Box>
                    <Input
                      type="file"
                      display="none"
                      accept="image/*"
                      onChange={e => {
                        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                        setPosterFile(file);
                        updateNestedField('poster', 'source', file ? file.name : '');
                        // Also pass the file and upload type to parent for upload handling
                        onChange({ 
                          ...formData, 
                          poster: { 
                            ...formData.poster, 
                            source: file ? file.name : '' 
                          },
                          posterFile: file,
                          posterType: posterType
                        });
                      }}
                    />
                  </CustomButton>
                )}
                <Box color="gray.500" fontSize="sm" display="flex" alignItems="center" gap={1}>
                  <MdAttachFile />
                  {posterFile ? posterFile.name : 'No file selected yet'}
                </Box>
              </Flex>
            ) : (
              <Input
                placeholder="Image URL"
                mb={2}
                value={formData.poster?.source || ''}
                onChange={e => updateNestedField('poster', 'source', e.target.value)}
              />
            )}
          </Flex>
        </Flex>

        <Box padding='16px' border='1px solid #DADADA' borderRadius='5px'>
          <Tabs.Root value={langTab} onValueChange={handleLangTabChange} variant="subtle" colorPalette="pink">
            <Tabs.List mb='24px' gap='24px'>
              {languages.map((lang: string, idx: number) => (
                <Tabs.Trigger key={lang} value={lang}>
                  {lang}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
            {languages.map((lang: string) => (
              <Tabs.Content key={lang} value={lang} asChild>
                <Box style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <Text fontWeight="medium" mb='8px'>Display Name</Text>
                  <Input
                    placeholder="Display Name"
                    mb='24px'
                    value={findTextDataByLanguage('displayName', lang)?.text || ''}
                    onChange={e => updateTextDataByLanguage('displayName', lang, e.target.value)}
                    onBlur={() => onBlur && onBlur(formData)}
                  />
                  <Text fontWeight="medium" mb='8px'>Description</Text>
                  <Textarea
                    placeholder="Description"
                    h='96px'
                    resize='none'
                    value={findTextDataByLanguage('description', lang)?.text || ''}
                    onChange={e => updateTextDataByLanguage('description', lang, e.target.value)}
                    onBlur={() => onBlur && onBlur(formData)}
                  />
                </Box>
              </Tabs.Content>
            ))}
          </Tabs.Root>
        </Box>
      </Box>
    );
  }

  if (isStepOption(selectedNode)) {
    // Step Option (sub-step) form
    return (
      <Box p={8} flex={1} overflowY='auto'>
        <Flex align="center" justify="space-between" mb={6}>
          <Text fontSize="2xl" fontWeight="bold">{selectedNode.label}</Text>
        </Flex>

        <Text fontWeight="medium" mb={2}>Generic Name</Text>
        <Input
          placeholder="Generic name"
          value={formData?.stepOptionName || ''}
          onChange={e => onChange({ ...formData, stepOptionName: e.target.value })}
          mb={6}
        />

        <Flex gap={6} mb={6}>
          <Box flex={1}>
            <Text fontWeight="medium" mb='8px'>Duration</Text>
            <Input
              placeholder="Duration in seconds"
              value={formData?.duration || ''}
              onChange={e => onChange({ ...formData, duration: e.target.value })}
            />
          </Box>
          <Box flex={1}>
            <Text fontWeight="medium" mb='8px'>Repetition</Text>
            <Input
              placeholder="Number of repetition"
              value={formData?.repetition || ''}
              onChange={e => onChange({ ...formData, repetition: e.target.value })}
            />
          </Box>
        </Flex>

        <Box padding='16px' border='1px solid #DADADA' borderRadius='5px' mb={6}>
          <Tabs.Root value={langTab} onValueChange={handleLangTabChange} variant="subtle" colorPalette="pink">
            <Tabs.List mb='24px' gap='24px'>
              {languages.map((lang: string, idx: number) => (
                <Tabs.Trigger key={lang} value={lang}>
                  {lang}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
            {languages.map((lang: string) => (
              <Tabs.Content key={lang} value={lang} asChild>
                <Box style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <Text fontWeight="medium" mb='8px'>Display Name</Text>
                  <Input
                    placeholder="Display Name"
                    value={formData?.displayName?.[lang] || ''}
                    onChange={e => onChange({ 
                      ...formData, 
                      displayName: { 
                        ...formData.displayName, 
                        [lang]: e.target.value 
                      } 
                    })}
                  />
                </Box>
              </Tabs.Content>
            ))}
          </Tabs.Root>
        </Box>

        {/* Constraints Section */}
        <Text fontWeight="bold" fontSize="lg" mb={4}>Constraints Steps</Text>

        <Box mb={6} borderWidth={1} borderRadius="lg" p={4} maxH='238px' overflowY='auto'>
          
          {/* Dynamic constraint multiselects */}
          {activeConstraints.map((constraint: any) => {
            const config = allConstraintOptions.find(opt => opt.type === constraint.type);
            if (!config) return null;
            
            // Convert stored original values to formatted values for display
            const formattedSelectedValues = constraint.values.map(convertOriginalValueToFormatted);
            const availableValues = config.options.filter((opt: string) => !formattedSelectedValues.includes(opt));
            
            return (
              <Flex align="center" mb={4} gap={4} key={constraint.type}>
                <Box flex={1}>
                  <Text mb={1} fontWeight="medium">{constraint.type}</Text>
                  <Menu.Root>
                    <Menu.Trigger style={{ width: '100%' }}>
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        p={2}
                        minH="48px"
                        display="flex"
                        flexWrap="wrap"
                        gap={1}
                        alignItems="center"
                        bg="white"
                        position="relative"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                      >
                        {/* Selected values as tags */}
                        {formattedSelectedValues.map((formattedValue: any, idx: any) => (
                          <Flex
                            key={idx}
                            align="center"
                            bg="gray.100"
                            borderRadius="full"
                            px={2}
                            py={1}
                            gap={1}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Text fontSize="sm" color="gray.700">{formattedValue}</Text>
                            <Box
                              as="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveConstraintValue(constraint.type, formattedValue);
                              }}
                              cursor="pointer"
                              color="gray.500"
                              _hover={{ color: 'gray.700' }}
                              fontSize="12px"
                              fontWeight="bold"
                            >
                              ×
                            </Box>
                          </Flex>
                        ))}
                        
                        {/* Placeholder when no values selected */}
                        {constraint.values.length === 0 && (
                          <Text color="gray.400" fontSize="sm">
                            Select {constraint.type}
                          </Text>
                        )}
                        
                        {/* Dropdown for remaining values */}
                        {availableValues.length > 0 && (
                          <Box
                            position="absolute"
                            right={2}
                            top="50%"
                            transform="translateY(-50%)"
                            display="flex"
                            alignItems="center"
                            color="gray.400"
                            pointerEvents="none"
                          >
                            <MdExpandMore />
                          </Box>
                        )}
                      </Box>
                    </Menu.Trigger>
                    <Menu.Positioner>
                      <Menu.Content>
                        {availableValues.map((value: string) => (
                          <Menu.Item 
                            key={value} 
                            onClick={() => handleAddConstraintValue(constraint.type, value)}
                          >
                            {value}
                          </Menu.Item>
                        ))}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Menu.Root>
                </Box>
                
                {/* Remove entire constraint button */}
                <CustomButton
                  visual="ghost"
                  alignSelf='flex-end'
                  aria-label={`Remove ${constraint.type}`}
                  onClick={() => handleRemoveConstraint(constraint.type)}
                  _hover={{ background: '#fc3c5ecc' }}
                  w='56px'
                  border='1px solid #DADADA'
                  borderRadius='5px'
                  h='48px'
                >
                  <MdDelete />
                </CustomButton>
              </Flex>
            );
          })}
          
          {/* Add new constraint type */}
          {availableConstraintTypes.length > 0 && (
            <Box w="100%">
              <Text mb={1} fontWeight="medium">Select constraints</Text>
              <Menu.Root w="100%">
                <Menu.Trigger style={{ width: '100%' }}>
                  <Box
                    w="100%"
                    px={4}
                    py={2}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="white"
                    textAlign="left"
                    cursor="pointer"
                    position="relative"
                    minH="48px"
                    display="flex"
                    alignItems="center"
                    color="gray.400"
                    _hover={{ bg: 'gray.50' }}
                  >
                    {availableConstraintTypes.join(', ')}
                    <Box position="absolute" right={3} top="50%" transform="translateY(-50%)" pointerEvents="none">
                      <MdExpandMore />
                    </Box>
                  </Box>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content>
                    {availableConstraintTypes.map((type: string) => (
                      <Menu.Item key={type} onClick={() => handleAddConstraint(type)}>
                        {type}
                      </Menu.Item>
                    ))}
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            </Box>
          )}
        </Box>

        {/* Exercises Section */}
        <Box>
          <Text fontWeight="bold" fontSize="lg" mb={4}>Exercises</Text>
          <CustomButton 
            visual="ghost" 
            color="#FC3C5E" 
            border="1px solid #FC3C5E" 
            h='30px'
            onClick={onAddExercise}
          >
            {formData?.exercises && formData.exercises.length > 0 ? 'Edit Exercise' : 'Add Exercise'}
          </CustomButton>
        </Box>
      </Box>
    );
  }

  // If a Step (not Step Option) is selected, you can show a Step form or nothing
  return (
    <Box p={8} flex={1}>
      <Text fontSize="xl" fontWeight="bold" mb={4}>Step Selected</Text>
    </Box>
  );
};

export function buildProgramMutationPayload(formData: any) {
  return {
    name: formData.name,
    status: formData.status,
    description: formData.description || [],
    displayName: formData.displayName || [],
    poster: formData.poster || null,
  };
}

export function buildProgramConstrainedMutationPayload(formData: any) {
  // Helper function to convert constraint array format to object format
  const convertConstraintsToObject = (constraints: any[]) => {
    if (!constraints || !Array.isArray(constraints)) return {};
    
    const constraintObject: any = {};
    constraints.forEach((constraint: any) => {
      if (constraint.type && constraint.values) {
        // Map constraint types to schema field names
        const fieldMap: Record<string, string> = {
          'Gender': 'genders',
          'Pathology': 'pathologies',
          'Sport': 'programSports',
          'Special program': 'specialPrograms',
          'Week focus': 'weekFocus',
          'User level': 'userLevel',
          'Category': 'categories',
          'Material': 'materials'
        };
        
        const fieldName = fieldMap[constraint.type];
        if (fieldName) {
          constraintObject[fieldName] = constraint.values;
        }
      }
    });
    return constraintObject;
  };

  // Helper function to build exercise option data
  const buildExerciseOptionData = (exerciseOptionData: any) => {
    if (!exerciseOptionData) return null;
    
    return {
      id: exerciseOptionData.id,
      duration: exerciseOptionData.duration,
      recuperation: exerciseOptionData.recuperation,
      repetitions: exerciseOptionData.repetitions,
      series: exerciseOptionData.series,
      exercise: exerciseOptionData.exerciseId ? {
        id: exerciseOptionData.exerciseId
      } : null,
      constraints: convertConstraintsToObject(exerciseOptionData.constraints)
    };
  };

  // Helper function to build exercise data
  const buildExerciseData = (exerciseData: any) => {
    if (!exerciseData) return null;
    
    return {
      constraints: convertConstraintsToObject(exerciseData.constraints || []),
      repetitions: exerciseData.repetitions,
      series: exerciseData.series,
      duration: exerciseData.duration,
      recuperation: exerciseData.recuperation,
      exerciseId: exerciseData.exerciseId || exerciseData.id
    };
  };

  // Helper function to build step option data
  const buildStepOptionData = (optionData: any) => {
    if (!optionData) return null;
    
    return {
      id: optionData.id,
      displayName: optionData.displayName || [],
      duration: optionData.duration,
      constraints: convertConstraintsToObject(optionData.constraints),
      exercises: (optionData.exercises || []).map((exercise: any) => buildExerciseData(exercise))
    };
  };

  // Helper function to build step data
  const buildStepData = (stepData: any) => {
    if (!stepData) return null;
    
    return {
      name: stepData.name || stepData.label,
      repetitions: stepData.repetitions,
      duration: stepData.duration,
      displayName: stepData.displayName || [],
      constraints: convertConstraintsToObject(stepData.constraints || []),
      exercises: (stepData.children || []).map((option: any) => buildExerciseData(option))
    };
  };

  return {
    name: formData.name,
    status: formData.status,
    description: formData.description || [],
    displayName: formData.displayName || [],
    poster: formData.poster || null,
    steps: formData.steps || []
  };
}

export default ProgramFormPanel; 